import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { load, setAddonSelection } from '../Actions/AddonProduct.action';
import ProductStockStatus from './ProductStockStatus';
import { translateFormat } from '../Services/translation';
import { formatPrice } from '../Services/formatPrice';

const collapsedCount = 2;
const maxQuantity = 999;

const fillIn = (text, args) => args.reduce((result, arg, index) => result.split(`{${index}}`).join(arg), text);

// The texts are new website strings, so a channel that lacks one falls back to the Swedish original.
const text = (key, fallback, ...args) => translateFormat(key, ...args) || fillIn(fallback, args);

const packSizeOf = (variant) => Math.max(1, variant.packSize || 1);

const roundToPack = (quantity, packSize) => Math.max(packSize, Math.ceil(quantity / packSize) * packSize);

// Same rule as the buy handler, which adds a whole pack also when no quantity field is rendered.
const readQuantity = (fieldId, packSize) => {
    const pack = Math.max(1, parseInt(packSize, 10) || 1);
    const field = fieldId && document.getElementById(fieldId);
    const value = field ? parseInt(field.value, 10) : NaN;
    return isNaN(value) || value < pack ? pack : Math.ceil(value / pack) * pack;
};

const useMainQuantity = (fieldId, packSize) => {
    const [quantity, setQuantity] = useState(() => readQuantity(fieldId, packSize));
    useEffect(() => {
        const field = fieldId && document.getElementById(fieldId);
        if (!field) {
            return undefined;
        }
        // The page's stepper script only rewrites the value attribute and fires no input event.
        const observer = new MutationObserver(() => setQuantity(readQuantity(fieldId, packSize)));
        observer.observe(field, { attributes: true, attributeFilter: ['value'] });
        return () => observer.disconnect();
    }, [fieldId, packSize]);
    return quantity;
};

// Opens in a new tab so the selection on this page survives a look at the add-on.
// The image repeats the name link, so it is kept out of the tab order and the accessibility tree.
const ProductLink = ({ url, className, hidden, children }) => url
    ? (
        <a
            href={url}
            target="_blank"
            rel="noopener"
            className={className}
            tabIndex={hidden ? -1 : undefined}
            aria-hidden={hidden ? 'true' : undefined}
        >{children}</a>
    )
    : <span className={className}>{children}</span>;

const AddonPrice = ({ variant }) => variant.formattedCampaignPrice
    ? <>
        + <span className="product__campaign-price">{variant.formattedCampaignPrice}</span>
        <span className="product__old-price">{variant.formattedPrice}</span>
    </>
    : <>+ {variant.formattedPrice}</>;

const GroupTitle = ({ group }) => (
    <div className="pdp-addons__group-head">
        <p className="pdp-addons__group-title">
            {group.name}
            {group.description && <span className="pdp-addons__group-description"> – {group.description}</span>}
        </p>
    </div>
);

const AddonItem = ({ variant, quantity, onToggle, onQuantity }) => {
    const packSize = packSizeOf(variant);
    const checked = quantity > 0;

    return (
        <div className={`pdp-addons__item${checked ? ' pdp-addons__item--selected' : ''}`}>
            <div className="pdp-addons__option">
                <ProductLink url={variant.url} className="pdp-addons__image" hidden>
                    {variant.defaultImageUrl && (
                        <img src={variant.defaultImageUrl} alt="" width="56" height="56" loading="lazy" decoding="async" />
                    )}
                </ProductLink>
                <span className="pdp-addons__text">
                    <ProductLink url={variant.url} className="pdp-addons__name">{variant.name}</ProductLink>
                    <span className="pdp-addons__price"><AddonPrice variant={variant} /></span>
                    {packSize > 1 && (
                        <span className="pdp-addons__pack">{translateFormat('product.soldinpack', packSize)}</span>
                    )}
                </span>
                <label className="pdp-addons__check">
                    <input
                        type="checkbox"
                        checked={checked}
                        aria-label={text('product.addons.add', 'Lägg till {0}', variant.name)}
                        onChange={() => onToggle(variant)}
                    />
                </label>
            </div>
            {checked && (
                <div className="pdp-addons__details">
                    <div className="pdp-addons__row">
                        <div className="pdp-addons__stepper" role="group" aria-label={`${text('product.quantity', 'Antal')}: ${variant.name}`}>
                            <button
                                type="button"
                                aria-label={text('product.quantity.decrease', 'Minska antal')}
                                disabled={quantity - packSize < packSize}
                                onClick={() => onQuantity(variant, quantity - packSize)}
                            >−</button>
                            <span aria-live="polite">{quantity}</span>
                            <button
                                type="button"
                                aria-label={text('product.quantity.increase', 'Öka antal')}
                                disabled={quantity + packSize > maxQuantity}
                                onClick={() => onQuantity(variant, quantity + packSize)}
                            >+</button>
                        </div>
                        <span className="pdp-addons__line-total">{formatPrice(quantity * variant.price)}</span>
                    </div>
                    {variant.deliveryStatus && (
                        <ProductStockStatus status={variant.deliveryStatus} statusText={variant.deliveryText} storeStatus="False" />
                    )}
                </div>
            )}
        </div>
    );
};

const PdpAddons = ({ baseProduct, product, price, packSize, quantityFieldId, addons }) => {
    const dispatch = useDispatch();
    const loadedSections = useSelector((state) => state.addons.addons);
    const selection = useSelector((state) => state.addons.selection);
    const [expanded, setExpanded] = useState(false);
    const [openGroups, setOpenGroups] = useState({});
    const collapseAnchor = useRef(null);
    const mainQuantity = useMainQuantity(quantityFieldId, packSize);
    const mainPrice = parseFloat(String(price || '0').replace(',', '.')) || 0;

    // The page renders the list with its data, so the first render matches the server markup instead of starting empty.
    const initialSections = useMemo(() => {
        try {
            return addons ? JSON.parse(addons).addOnSections || [] : null;
        } catch (err) {
            return null;
        }
    }, [addons]);
    const sections = initialSections || loadedSections;

    useEffect(() => {
        if (!initialSections) {
            dispatch(load(baseProduct));
        }
    }, [baseProduct]);

    const groups = useMemo(() => {
        const seen = {};
        return (sections || [])
            .map((section) => ({
                name: (section.name || '').trim(),
                description: section.description,
                variants: (section.configVariants || []).filter((variant) => {
                    if (variant.soldOut || !variant.sku || seen[variant.sku]) {
                        return false;
                    }
                    seen[variant.sku] = true;
                    return true;
                }),
            }))
            .filter((group) => group.variants.length)
            .map((group, index) => ({ ...group, key: index }));
    }, [sections]);
    const variants = useMemo(() => groups.reduce((all, group) => all.concat(group.variants), []), [groups]);

    // With several sections the collapsed list shows the start of each of the first two, so more than one type is visible.
    // A section with exactly two add-ons shows both rather than a link to show one more.
    const collapsedSkus = useMemo(() => {
        const picked = groups.length > 1
            ? groups.slice(0, 2).reduce((all, group) => all.concat(group.variants.slice(0, group.variants.length === 2 ? 2 : 1)), [])
            : (groups.length ? groups[0].variants.slice(0, collapsedCount) : []);
        return picked.reduce((map, variant) => ({ ...map, [variant.sku]: true }), {});
    }, [groups]);

    const quantities = useMemo(
        () => selection.reduce((map, row) => ({ ...map, [row.articleNumber]: row.quantity }), {}),
        [selection]
    );
    const addonCount = selection.reduce((sum, row) => sum + row.quantity, 0);
    const total = mainPrice * mainQuantity + selection.reduce((sum, row) => sum + row.quantity * row.price, 0);

    useEffect(() => {
        document.dispatchEvent(new CustomEvent('pdp:addons', {
            detail: selection.length
                ? { label: text('product.addons.sticky', '+ {0} tillval', addonCount), total: formatPrice(total) }
                : null,
        }));
    }, [addonCount, total, selection.length]);

    // Keeps the clicked toggle in place on collapse; browser scroll anchoring is paused since it shifts the page mid-measure.
    useLayoutEffect(() => {
        const anchor = collapseAnchor.current;
        if (!anchor) {
            return;
        }
        collapseAnchor.current = null;
        if (anchor.element.isConnected) {
            const shift = anchor.element.getBoundingClientRect().top - anchor.top;
            if (shift < 0) {
                window.scrollBy({ top: shift, behavior: 'instant' });
            }
        }
        requestAnimationFrame(() => {
            document.documentElement.style.overflowAnchor = '';
        });
    });

    const keepInView = (event) => {
        document.documentElement.style.overflowAnchor = 'none';
        collapseAnchor.current = { element: event.currentTarget, top: event.currentTarget.getBoundingClientRect().top };
    };

    const update = (next) => dispatch(setAddonSelection(product, next));

    const onToggle = (variant) => {
        if (quantities[variant.sku]) {
            update(selection.filter((row) => row.articleNumber !== variant.sku));
            return;
        }
        // Starts at the main product's quantity once; after that the two quantities are independent.
        const quantity = Math.min(roundToPack(readQuantity(quantityFieldId, packSize), packSizeOf(variant)), maxQuantity);
        update([...selection, { articleNumber: variant.sku, quantity, price: variant.price }]);
    };

    const onQuantity = (variant, quantity) => update(selection.map((row) => (
        row.articleNumber === variant.sku ? { ...row, quantity } : row
    )));

    if (!variants.length) {
        return null;
    }

    // A selected add-on stays visible when the list is collapsed, so the total never includes a hidden row.
    const groupOf = (variant) => groups.find((group) => group.variants.indexOf(variant) >= 0);
    const isVisible = (variant) => expanded || collapsedSkus[variant.sku] || quantities[variant.sku] || openGroups[groupOf(variant).key];
    const visibleGroups = groups
        .map((group) => ({ ...group, variants: group.variants.filter(isVisible) }))
        .filter((group) => group.variants.length);
    const hiddenCount = variants.length - visibleGroups.reduce((sum, group) => sum + group.variants.length, 0);
    const shownGroups = visibleGroups.map((visible) => ({ ...visible, all: groups[visible.key].variants }));
    const showAll = !expanded && hiddenCount > 0;

    // Only with several sections; with one, the button below does the same thing.
    const groupToggle = (group) => {
        const open = openGroups[group.key];
        if (expanded || groups.length < 2 || group.all.length < 2 || (!open && group.variants.length === group.all.length)) {
            return null;
        }
        return (
            <button
                type="button"
                className="pdp-addons__group-toggle"
                aria-expanded={!!open}
                onClick={(event) => {
                    if (open) {
                        keepInView(event);
                    }
                    setOpenGroups({ ...openGroups, [group.key]: !open });
                }}
            >
                {open
                    ? text('product.addons.group-show-less', 'Visa färre')
                    : text('product.addons.group-show-all', 'Visa alla ({0})', group.all.length)}
            </button>
        );
    };

    const onMore = (event) => {
        if (showAll) {
            setExpanded(true);
            return;
        }
        keepInView(event);
        setExpanded(false);
        setOpenGroups({});
    };

    return (
        <>
            <fieldset className="pdp-addons" aria-label={text('product.addons.title', 'Komplettera ditt köp')}>
                {shownGroups.map((group) => ({ ...group, toggle: groupToggle(group) })).map((group) => (
                    <div className="pdp-addons__group" role="group" aria-label={group.name || undefined} key={group.key}>
                        {group.name && <GroupTitle group={group} />}
                        <div className="pdp-addons__list">
                            {group.variants.map((variant) => (
                                <AddonItem
                                    key={variant.sku}
                                    variant={variant}
                                    quantity={quantities[variant.sku] || 0}
                                    onToggle={onToggle}
                                    onQuantity={onQuantity}
                                />
                            ))}
                        </div>
                        {group.toggle}
                    </div>
                ))}
                {(showAll || expanded || Object.keys(openGroups).some((key) => openGroups[key])) && (
                    <button type="button" className="pdp-addons__more" aria-expanded={!showAll} onClick={onMore}>
                        {showAll
                            ? text('product.addons.show-all', 'Visa alla {0} tillval', variants.length)
                            : text('product.addons.show-less', 'Visa färre tillval')}
                    </button>
                )}
            </fieldset>
            {selection.length > 0 && (
                <div className="pdp-addons__total">
                    <span>{text('product.addons.total', 'Totalt, {0} produkter', mainQuantity + addonCount)}</span>
                    <span className="pdp-addons__total-price">{formatPrice(total)}</span>
                </div>
            )}
        </>
    );
};

export default PdpAddons;
